import { addMinutes, format, startOfDay } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'

import { DAY_NAMES_NARROW } from '@/constants'
import { MarketShopProductModel, MarketShopSettingsModel, NullableType } from '@/models'
import { formatWeekday, generateId, isEmpty } from '@/utils'

export const getMarketShopImageHref = (fileName: MarketShopProductModel['image']) =>
  `${import.meta.env.VITE_ASSETS_URL}/market-images/${fileName}`

export const createMarketShopSessionId = () => {
  return generateId('market-shop-session')
}

export const formatMinutesToTime = (minutes: number) => {
  const date = addMinutes(startOfDay(new Date()), minutes)
  return format(date, 'h:mm a').toUpperCase()
}

export interface GetMarketShopStatusResult {
  isOpen: boolean
  nextEventText: string
}

export const getMarketShopStatus = (
  settings: NullableType<MarketShopSettingsModel>,
  timezone: string,
  t: TFunction<['market-shop']>,
): GetMarketShopStatusResult => {
  if (!settings || isEmpty(settings.businessHours)) return { isOpen: false, nextEventText: '' }

  const now = toZonedTime(new Date(), timezone)
  const currentDayIndex = now.getDay()
  const currentDayName = DAY_NAMES_NARROW[currentDayIndex]
  const currentMinutes = now.getHours() * 60 + now.getMinutes()

  const todayHours = settings.businessHours.find((h) => h.name === currentDayName)

  if (todayHours && currentMinutes >= todayHours.startTime && currentMinutes < todayHours.endTime) {
    return {
      isOpen: true,
      nextEventText: t('market-shop:workingHours.closesAt', {
        time: formatMinutesToTime(todayHours.endTime),
      }),
    }
  }

  // Find the next opening time
  // Check later today
  if (todayHours && currentMinutes < todayHours.startTime) {
    return {
      isOpen: false,
      nextEventText: t('market-shop:workingHours.opensAt', {
        time: formatMinutesToTime(todayHours.startTime),
      }),
    }
  }

  // Check upcoming days
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7
    const nextDayName = DAY_NAMES_NARROW[nextDayIndex]
    const nextDayHours = settings.businessHours.find((h) => h.name === nextDayName)

    if (nextDayHours) {
      return {
        isOpen: false,
        nextEventText: t('market-shop:workingHours.opensOnDay', {
          time: formatMinutesToTime(nextDayHours.startTime),
          weekday: formatWeekday(nextDayName, 'short'),
        }),
      }
    }
  }

  return { isOpen: false, nextEventText: '' }
}
